// utils/time.js
import moment from 'moment';

/**
 * Find the system's STANDARD (non-DST) offset in minutes for a given year.
 * JS getTimezoneOffset(): minutes to add to LOCAL to get UTC (DST has smaller value).
 * We take the MAX across months → standard time (no DST).
 * For dual-offset zones, pair with {@link systemDaylightOffsetMinutes} (min across months);
 * fixed-offset zones yield the same value for both.
 */
export function systemStandardOffsetMinutes(year = new Date().getFullYear()) {
  const offsets = [];
  for (let m = 0; m < 12; m++) {
    // Use UTC mid-month to avoid edge cases
    const d = new Date(Date.UTC(year, m, 15, 12, 0, 0));
    offsets.push(d.getTimezoneOffset());
  }
  return Math.max(...offsets); // e.g. 300 for America/New_York standard (EST)
}

/**
 * Find the system's DST (daylight) offset in minutes for a given year, for zones
 * with two offsets. We take the MIN across months → daylight time.
 * For fixed-offset zones, min equals max (same as {@link systemStandardOffsetMinutes}).
 */
export function systemDaylightOffsetMinutes(year = new Date().getFullYear()) {
  const offsets = [];
  for (let m = 0; m < 12; m++) {
    const d = new Date(Date.UTC(year, m, 15, 12, 0, 0));
    offsets.push(d.getTimezoneOffset());
  }
  return Math.min(...offsets); // e.g. 240 for America/New_York daylight (EDT)
}

/**
 * Build a +HH:mm / -HH:mm suffix for API/datetime strings: sign from "now" and
 * magnitude from {@link systemDaylightOffsetMinutes} for the given calendar year
 * (event/ticket date). Matches PostEvent getTimeZone / zz behavior.
 * @param {number} [year] - Calendar year to sample; invalid/missing uses current year
 * @returns {string} e.g. "-04:00"
 */
export function formatDaylightOffsetSuffix(year) {
  const y =
    typeof year === 'number' && Number.isFinite(year)
      ? year
      : new Date().getFullYear();
  const offset = new Date().getTimezoneOffset();
  const o = Math.abs(systemDaylightOffsetMinutes(y));
  return (
    (offset < 0 ? '+' : '-') +
    ('00' + Math.floor(o / 60)).slice(-2) +
    ':' +
    ('00' + (o % 60)).slice(-2)
  );
}

/**
 * Convert UTC -> "system local" using a fixed offset (no DST rules at parse time).
 * @param {string|number|Date} input - UTC time (e.g., "2024-07-04 16:00:00+00", ISO, epoch s/ms, Date)
 * @param {Object} [opts]
 * @param {string} [opts.outputFormat='YYYY-MM-DD HH:mm:ss'] - Moment format
 * @param {number} [opts.year] - Year to derive offset from (defaults to current year)
 * @param {'daylight'|'standard'} [opts.offsetBasis='daylight'] - Use yearly min (DST) or max (standard) offset
 * @param {string|number} [opts.overrideOffset] - Optional fixed offset to force:
 *        string: "+05:00" / "-04:30" / "+0530"; number: minutes from UTC (east positive)
 * @returns {string}
 */
export function utcToSystemFixedLocal(
  input,
  { outputFormat = 'YYYY-MM-DD HH:mm:ss', year, offsetBasis = 'daylight', overrideOffset } = {}
) {
  // 1) Parse input as UTC (strict)
  let m;
  if (typeof input === 'number') {
    m = input < 1e12 ? moment.unix(input).utc() : moment.utc(input);
  } else if (input instanceof Date) {
    m = moment.utc(input);
  } else {
    m = moment.utc(
      input,
      [
        'YYYY-MM-DD HH:mm:ssZ',
        'YYYY-MM-DD HH:mm:ssZZ',
        moment.ISO_8601,
        'X',
        'x',
      ],
      true
    );
  }
  if (!m.isValid()) throw new Error('Invalid UTC timestamp');

  // 2) Resolve fixed offset (minutes, east positive for moment.utcOffset)
  const toMinutes = (val) => {
    if (typeof val === 'number' && Number.isFinite(val)) return val; // already minutes (east +)
    if (typeof val === 'string') {
      const s = val.replace(':', ''); // "+0530"
      const mm = /^([+-])(\d{2})(\d{2})$/.exec(s);
      if (!mm) throw new Error('Bad offset string. Use "+HH:mm" or "-HH:mm".');
      const sign = mm[1] === '-' ? -1 : 1;
      const hh = parseInt(mm[2], 10);
      const mi = parseInt(mm[3], 10);
      return sign * (hh * 60 + mi);
    }
    const y = year != null ? year : new Date().getFullYear();
    const jsOff =
      offsetBasis === 'standard'
        ? systemStandardOffsetMinutes(y)
        : systemDaylightOffsetMinutes(y);
    return -jsOff;
  };

  const fixedOffsetMin = toMinutes(overrideOffset);

  // 3) Apply the fixed offset (NO DST rules involved)
  return m.utcOffset(fixedOffsetMin).format(outputFormat);
}
